import React, { type ChangeEventHandler, type FocusEventHandler, type ForwardedRef, forwardRef } from 'react';

import { clamp } from '@pkg/utils';

import { PrimitiveInputRange } from './InputRange.style';

// React normalizes `onChange` and `onInput`, so behaviour of `onChange`
// is that it will fire on every pixel drag (instead of on pointer release).
// Use `onBlur` if you need "change end / native HTML" behaviour.
export type InputRangeFocus = FocusEventHandler<HTMLInputElement>;
export type InputRangeChange = ChangeEventHandler<HTMLInputElement>;

export interface InputRangeProps {
    id?: string;
    name?: string;
    value?: number;
    min?: number;
    max?: number;
    step?: number | 'any';
    ariaLabel?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    // Required to add a `data-input-error` attribute for easier styling.
    error?: boolean;
    readOnly?: boolean;
    required?: boolean;
    onBlur?: InputRangeFocus;
    onFocus?: InputRangeFocus;
    onChange?: InputRangeChange;
    // Required for `styled-components` to append additional classes.
    className?: string;
}

function InputRangeComponent(
    {
        value = 0,
        min = 0,
        max = 100,
        disabled = false,
        error = false,
        readOnly = false,
        ariaLabel,
        onBlur,
        onFocus,
        onChange,
        ...rest
    }: InputRangeProps,
    ref: ForwardedRef<HTMLInputElement>,
) {
    const noInteraction = Boolean(disabled || readOnly);

    return (
        <PrimitiveInputRange
            ref={ref}
            {...rest}
            data-input-error={error ? true : undefined}
            aria-label={ariaLabel}
            value={clamp(min, value, max)}
            min={min}
            max={max}
            disabled={disabled}
            readOnly={readOnly}
            onBlur={noInteraction ? undefined : onBlur}
            onFocus={noInteraction ? undefined : onFocus}
            onChange={noInteraction ? undefined : onChange}
        />
    );
}

export const InputRange = forwardRef(InputRangeComponent);
